import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Divider,
  Switch,
  Tag,
  InputNumber,
  Row,
  Col,
  Modal,
  Spin,
  Typography,
} from "antd";
import {
  SaveOutlined,
  DeleteOutlined,
  DragOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "react-toastify";
import {
  getFlowById,
  createFlow,
  updateFlow,
  addStageToFlow,
  updateStage,
  deleteStage,
  reorderStages,
} from "../../api/productionApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { TextArea } = Input;
const { Text } = Typography;

// ── Predefined stage templates ───────────────────────────────
// These are the common corrugated box manufacturing stages.
// The admin selects one to quickly add it to a flow.
const STAGE_TEMPLATES = [
  {
    code: "pre_print",
    name: "Pre-Process Printing (Offset/Flexo)",
    stage_type: "production",
    machine_type: "offset",
    estimated_hours: 2,
    color: "#22C55E",
    is_optional: true,
  },
  {
    code: "coating",
    name: "Coating / Lamination",
    stage_type: "production",
    machine_type: "coating",
    estimated_hours: 1,
    color: "#06B6D4",
    is_optional: true,
  },
  {
    code: "corrugation",
    name: "Corrugation",
    stage_type: "production",
    machine_type: "corrugation",
    estimated_hours: 2,
    color: "#EF4444",
  },
  {
    code: "sheet_cutting",
    name: "Sheet Cutting",
    stage_type: "production",
    machine_type: "sheet_cutting",
    estimated_hours: 1,
    color: "#F59E0B",
  },
  {
    code: "pasting",
    name: "Pasting",
    stage_type: "production",
    machine_type: null,
    estimated_hours: 1.5,
    color: "#EAB308",
  },
  {
    code: "casting",
    name: "Casting",
    stage_type: "production",
    machine_type: "die_cutting",
    estimated_hours: 1,
    color: "#8B5CF6",
  },
  {
    code: "joining",
    name: "Joining (Stitch/Glue)",
    stage_type: "production",
    machine_type: null,
    estimated_hours: 1,
    color: "#EC4899",
  },
  {
    code: "after_box_print",
    name: "After-Box Printing (Screen/RS4)",
    stage_type: "production",
    machine_type: "screen_printing",
    estimated_hours: 1.5,
    color: "#10B981",
    is_optional: true,
  },
  {
    code: "quality_check",
    name: "Quality Check",
    stage_type: "qc",
    machine_type: null,
    estimated_hours: 0.5,
    color: "#14B8A6",
  },
  {
    code: "ready_for_dispatch",
    name: "Ready for Dispatch",
    stage_type: "logistics",
    machine_type: null,
    estimated_hours: 0.5,
    color: "#10B981",
  },
  {
    code: "dispatched",
    name: "Dispatched",
    stage_type: "logistics",
    machine_type: null,
    estimated_hours: 0,
    color: "#059669",
  },
];

// Box type options matching the API's applicable_box_types field
const BOX_TYPE_OPTIONS = [
  { value: "regular_folding_carton", label: "Regular Folding Carton" },
  { value: "flat_mailer", label: "Flat Mailer" },
  { value: "rsc", label: "RSC (Regular Slotted Container)" },
  { value: "hsc", label: "HSC (Half Slotted Container)" },
  { value: "fol", label: "FOL (Full Overlap Container)" },
  { value: "die_cut", label: "Die-Cut Box" },
  { value: "sheet", label: "Plain Sheet" },
  { value: "partition", label: "Partition" },
  { value: "top_bottom", label: "Top & Bottom" },
  { value: "telescoping", label: "Telescoping Box" },
];

// ═══════════════════════════════════════════════════════════════
// Sortable Stage Item
// ═══════════════════════════════════════════════════════════════
const SortableStage = ({ stage, index, onEdit, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id || stage._tempId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? "#e6f7ff" : "#fafafa",
    border: `1px solid ${isDragging ? "#1890ff" : "#d9d9d9"}`,
    borderLeft: `4px solid ${stage.color || "#d9d9d9"}`,
    borderRadius: 6,
    padding: "10px 14px",
    marginBottom: 8,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Space>
          <span
            {...attributes}
            {...listeners}
            style={{ cursor: "grab", padding: "0 4px" }}
          >
            <DragOutlined style={{ color: "#999", fontSize: 16 }} />
          </span>
          <Tag
            style={{
              borderRadius: "50%",
              width: 24,
              height: 24,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {index + 1}
          </Tag>
          <strong>{stage.name}</strong>
          <Tag style={{ fontSize: 11 }}>{stage.code}</Tag>
          {stage.stage_type && stage.stage_type !== "production" && (
            <Tag color="purple" style={{ fontSize: 11 }}>
              {stage.stage_type.toUpperCase()}
            </Tag>
          )}
          {stage.machine_type && (
            <Tag color="blue" style={{ fontSize: 11 }}>
              Machine: {stage.machine_type}
            </Tag>
          )}
          {stage.requires_qc && (
            <Tag color="orange" style={{ fontSize: 11 }}>
              QC
            </Tag>
          )}
          {stage.is_optional && (
            <Tag color="cyan" style={{ fontSize: 11 }}>
              OPTIONAL
            </Tag>
          )}
          {stage.estimated_hours && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              ~{stage.estimated_hours}h
            </Text>
          )}
        </Space>
        <Space>
          <Button
            icon={<SettingOutlined />}
            size="small"
            onClick={() => onEdit(stage)}
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => onRemove(stage)}
          />
        </Space>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════
const ProductionFlowEditor = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── Route params ───────────────────────────────────────────
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  // ── State ──────────────────────────────────────────────────
  const [form] = Form.useForm();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingStage, setEditingStage] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ── Load existing flow ─────────────────────────────────────
  const fetchFlow = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFlowById(id);
      form.setFieldsValue({
        name: data.name,
        description: data.description,
        applicable_box_types: data.applicable_box_types || [],
        is_default: !!data.is_default,
        is_active: data.is_active !== false,
      });
      const sortedStages = (data.stages || []).sort(
        (a, b) => (a.sequence || 0) - (b.sequence || 0)
      );
      setStages(sortedStages);
    } catch (error) {
      toast.error("Failed to load flow");
      navigate("/settings/production-flow");
    } finally {
      setLoading(false);
    }
  }, [id, form, navigate]);

  useEffect(() => {
    if (!isNew) {
      fetchFlow();
    }
  }, [isNew, fetchFlow]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Drag-and-drop ──────────────────────────────────────────
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setStages((prev) => {
      const oldIndex = prev.findIndex(
        (s) => (s.id || s._tempId) === active.id
      );
      const newIndex = prev.findIndex(
        (s) => (s.id || s._tempId) === over.id
      );
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // ── Add stage from template ────────────────────────────────
  const handleAddStage = (templateCode) => {
    const template = STAGE_TEMPLATES.find((t) => t.code === templateCode);
    if (!template) return;

    // Check if stage code already exists in the list
    if (stages.some((s) => s.code === template.code)) {
      toast.warning(`Stage "${template.name}" is already in the flow`);
      return;
    }

    const newStage = {
      ...template,
      _tempId: `new-${Date.now()}`, // temp id for DnD
      buffer_applies: false,
      auto_advance: false,
      requires_qc: false,
    };
    setStages((prev) => [...prev, newStage]);
  };

  // ── Remove stage ───────────────────────────────────────────
  const handleRemoveStage = (stage) => {
    const identifier = stage.name;
    Modal.confirm({
      title: "Remove Stage",
      content: `Remove "${identifier}" from this flow?`,
      okText: "Remove",
      okType: "danger",
      onOk: () => {
        setStages((prev) =>
          prev.filter((s) => (s.id || s._tempId) !== (stage.id || stage._tempId))
        );
      },
    });
  };

  // ── Update stage from config modal ─────────────────────────
  const handleUpdateStage = (stageIdentifier, updates) => {
    setStages((prev) =>
      prev.map((s) =>
        (s.id || s._tempId) === stageIdentifier ? { ...s, ...updates } : s
      )
    );
    setEditingStage(null);
  };

  // ── Save ───────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (stages.length === 0) {
        toast.error("Add at least one stage to the flow");
        return;
      }

      setSaving(true);

      if (isNew) {
        // CREATE: send full payload with embedded stages
        const payload = {
          name: values.name,
          description: values.description || "",
          applicable_box_types: values.applicable_box_types || [],
          is_default: !!values.is_default,
          stages: stages.map((s) => ({
            code: s.code,
            name: s.name,
            stage_type: s.stage_type || "production",
            machine_type: s.machine_type || undefined,
            estimated_hours: s.estimated_hours || undefined,
            buffer_applies: !!s.buffer_applies,
            auto_advance: !!s.auto_advance,
            requires_qc: !!s.requires_qc,
            color: s.color || undefined,
          })),
        };
        await createFlow(payload);
        toast.success("Production flow created");
      } else {
        // UPDATE flow metadata
        await updateFlow(id, {
          name: values.name,
          description: values.description || "",
          applicable_box_types: values.applicable_box_types || [],
          is_default: !!values.is_default,
          is_active: values.is_active !== false,
        });

        // For existing flows, we need to handle stages individually:
        // 1. Identify new stages (those with _tempId) → addStageToFlow
        // 2. Identify existing stages → updateStage
        // 3. Identify removed stages → deleteStage
        // 4. Reorder stages

        // Fetch current state to know what exists
        const currentFlow = await getFlowById(id);
        const existingStageIds = (currentFlow.stages || []).map((s) => s.id);
        const updatedStageIds = stages.filter((s) => s.id).map((s) => s.id);

        // Delete removed stages
        const toDelete = existingStageIds.filter(
          (sid) => !updatedStageIds.includes(sid)
        );
        for (const stageId of toDelete) {
          try {
            await deleteStage(stageId);
          } catch (_) {
            /* ignore if already deleted */
          }
        }

        // Add new stages
        for (const stage of stages) {
          if (stage._tempId && !stage.id) {
            try {
              const created = await addStageToFlow(id, {
                code: stage.code,
                name: stage.name,
                stage_type: stage.stage_type || "production",
                machine_type: stage.machine_type || undefined,
                estimated_hours: stage.estimated_hours || undefined,
                buffer_applies: !!stage.buffer_applies,
                auto_advance: !!stage.auto_advance,
                requires_qc: !!stage.requires_qc,
                is_optional: !!stage.is_optional,
                color: stage.color || undefined,
              });
              // Replace temp id with real id for reorder
              stage.id = created.id;
            } catch (err) {
              toast.error(`Failed to add stage "${stage.name}"`);
            }
          }
        }

        // Update existing stages that may have changed
        for (const stage of stages) {
          if (stage.id && existingStageIds.includes(stage.id)) {
            try {
              await updateStage(stage.id, {
                name: stage.name,
                stage_type: stage.stage_type || "production",
                machine_type: stage.machine_type || undefined,
                estimated_hours: stage.estimated_hours || undefined,
                buffer_applies: !!stage.buffer_applies,
                auto_advance: !!stage.auto_advance,
                requires_qc: !!stage.requires_qc,
                is_optional: !!stage.is_optional,
                color: stage.color || undefined,
              });
            } catch (_) {
              /* best effort */
            }
          }
        }

        // Reorder: POST /v1/production/stage/flow/:flowId/reorder
        const orderedIds = stages.map((s) => s.id).filter(Boolean);
        if (orderedIds.length > 0) {
          try {
            await reorderStages(id, orderedIds);
          } catch (_) {
            /* best effort */
          }
        }

        toast.success("Production flow updated");
      }

      navigate("/settings/production-flow");
    } catch (error) {
      if (error.errorFields) return; // form validation
      toast.error(error.response?.data?.message || "Failed to save flow");
    } finally {
      setSaving(false);
    }
  };

  // ── Available templates (filter out already-added) ─────────
  const availableTemplates = STAGE_TEMPLATES.filter(
    (t) => !stages.some((s) => s.code === t.code)
  );

  // ── Render ─────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <PageTitle title="Back" subtitle="Loading Flow..." />
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="large" />
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle
        title="Back"
        subtitle={isNew ? "Create Production Flow" : "Edit Production Flow"}
      />

      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/settings/production-flow")}
            />
            <span>{isNew ? "Create Production Flow" : "Edit Production Flow"}</span>
          </Space>
        }
      >
        {/* ── Flow metadata form ────────────────────────────── */}
        <Form
          form={form}
          layout="vertical"
          initialValues={{ is_active: true, is_default: false }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Flow Name"
                rules={[{ required: true, message: "Enter a flow name" }]}
              >
                <Input placeholder="e.g., Standard 3-Ply Box Flow" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="applicable_box_types"
                label="Applicable Box Types"
                rules={[
                  {
                    required: true,
                    message: "Select at least one box type",
                    type: "array",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select box types this flow applies to"
                  options={BOX_TYPE_OPTIONS}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={2}
              placeholder="Describe this production flow..."
            />
          </Form.Item>

          <Space size="large">
            <Form.Item
              name="is_default"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch
                checkedChildren="Default Flow"
                unCheckedChildren="Not Default"
              />
            </Form.Item>
            {!isNew && (
              <Form.Item
                name="is_active"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                />
              </Form.Item>
            )}
          </Space>
        </Form>

        <Divider orientation="left">
          Production Stages ({stages.length})
        </Divider>

        {/* ── Add stage dropdown ────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Select
              placeholder="Add stage from template…"
              style={{ width: 280 }}
              onChange={handleAddStage}
              value={null}
              notFoundContent="All templates already added"
            >
              {availableTemplates.map((t) => (
                <Select.Option key={t.code} value={t.code}>
                  <Space>
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: t.color,
                      }}
                    />
                    {t.name}
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ({t.code})
                    </Text>
                  </Space>
                </Select.Option>
              ))}
            </Select>
            <Text type="secondary">or drag to reorder existing stages</Text>
          </Space>
        </div>

        {/* ── Sortable stage list ───────────────────────────── */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={stages.map((s) => s.id || s._tempId)}
            strategy={verticalListSortingStrategy}
          >
            {stages.map((stage, index) => (
              <SortableStage
                key={stage.id || stage._tempId}
                stage={stage}
                index={index}
                onEdit={setEditingStage}
                onRemove={handleRemoveStage}
              />
            ))}
          </SortableContext>
        </DndContext>

        {stages.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: 32,
              color: "#999",
              background: "#fafafa",
              borderRadius: 8,
              border: "1px dashed #d9d9d9",
            }}
          >
            No stages added yet. Select a stage template above to start
            building this flow.
          </div>
        )}

        <Divider />

        <Space>
          <Button onClick={() => navigate("/settings/production-flow")}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={stages.length === 0}
          >
            {isNew ? "Create Flow" : "Save Changes"}
          </Button>
        </Space>
      </Card>

      {/* ── Stage Config Modal ──────────────────────────────── */}
      <StageConfigModal
        stage={editingStage}
        onClose={() => setEditingStage(null)}
        onSave={handleUpdateStage}
      />
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// Stage Configuration Modal
// ═══════════════════════════════════════════════════════════════
const StageConfigModal = ({ stage, onClose, onSave }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (stage) {
      form.setFieldsValue({
        name: stage.name,
        stage_type: stage.stage_type || "production",
        machine_type: stage.machine_type || undefined,
        estimated_hours: stage.estimated_hours || undefined,
        color: stage.color || "#1890ff",
        buffer_applies: !!stage.buffer_applies,
        auto_advance: !!stage.auto_advance,
        requires_qc: !!stage.requires_qc,
        is_optional: !!stage.is_optional,
      });
    }
  }, [stage, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      onSave(stage.id || stage._tempId, {
        name: values.name,
        stage_type: values.stage_type,
        machine_type: values.machine_type || null,
        estimated_hours: values.estimated_hours || null,
        color: values.color,
        buffer_applies: !!values.buffer_applies,
        auto_advance: !!values.auto_advance,
        requires_qc: !!values.requires_qc,
        is_optional: !!values.is_optional,
      });
    } catch (_) {
      /* validation error */
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          Configure Stage: {stage?.name || ""}
        </Space>
      }
      open={!!stage}
      onCancel={onClose}
      onOk={handleSave}
      okText="Apply"
      width={520}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name="name"
              label="Stage Name"
              rules={[{ required: true, message: "Enter stage name" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="color" label="Color">
              <Input type="color" style={{ width: "100%", height: 32 }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="stage_type" label="Stage Type">
              <Select>
                <Select.Option value="production">Production</Select.Option>
                <Select.Option value="qc">Quality Check</Select.Option>
                <Select.Option value="dispatch">Dispatch</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="machine_type" label="Machine Type">
              <Select allowClear placeholder="None (manual)">
                <Select.Option value="corrugation">Corrugation</Select.Option>
                <Select.Option value="sheet_cutting">Sheet Cutting</Select.Option>
                <Select.Option value="printing">Printing</Select.Option>
                <Select.Option value="casting">Casting</Select.Option>
                <Select.Option value="coating">Coating</Select.Option>
                <Select.Option value="stitching">Stitching</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="estimated_hours" label="Est. Duration (hours)">
              <InputNumber
                min={0.1}
                step={0.5}
                style={{ width: "100%" }}
                placeholder="e.g., 2"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Code"
              style={{ marginBottom: 0 }}
            >
              <Input
                value={stage?.code || ""}
                disabled
                style={{ background: "#f5f5f5" }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Stage code cannot be changed
              </Text>
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "12px 0" }} />

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="requires_qc"
              valuePropName="checked"
              label="QC Required"
            >
              <Switch size="small" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="buffer_applies"
              valuePropName="checked"
              label="Buffer Applies"
            >
              <Switch size="small" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="auto_advance"
              valuePropName="checked"
              label="Auto Advance"
            >
              <Switch size="small" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="is_optional"
              valuePropName="checked"
              label="Optional Stage"
              tooltip="Optional stages are auto-skipped if job specs don't require them (e.g. printing type determines pre-print vs after-box print)"
            >
              <Switch size="small" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ProductionFlowEditor;
